/**
 * WeightsCore's `TrainingPlanText`, as the builder needs it before the shared reader exports its
 * own copy (DESIGN §17.4). `lib.ts` prefers the package's functions when they exist.
 */

export function fold(text: string): string {
  const plain = text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
  let out = "";
  let lastWasSpace = true;
  for (const character of plain) {
    if (/[\p{L}\p{N}]/u.test(character)) {
      out += character;
      lastWasSpace = false;
    } else if (!lastWasSpace) {
      out += " ";
      lastWasSpace = true;
    }
  }
  return out.trim();
}

export function words(text: string): string[] {
  const folded = fold(text);
  return folded ? folded.split(" ") : [];
}

function trailingDigits(token: string): string {
  const match = /\d+$/.exec(token);
  return match ? match[0] : "";
}

function trailingNumber(token: string): number | null {
  const digits = trailingDigits(token);
  if (!digits || digits.length > 2) return null;
  if (token.length - digits.length > 2) return null;
  return Number(digits);
}

function splitTrailingNumber(token: string): [string, number] | null {
  const digits = trailingDigits(token);
  if (!digits || digits.length > 2) return null;
  const head = token.slice(0, token.length - digits.length);
  if (head.length < 3) return null;
  return [head, Number(digits)];
}

/** `Weights #2`, `Weights N2` and `Weights2` share the key `weights #2`. */
export function labelKey(text: string): string {
  const parts = words(text);
  if (parts.length === 0) return "";
  const last = parts[parts.length - 1]!;
  const number = parts.length > 1 ? trailingNumber(last) : null;
  if (number !== null) {
    parts[parts.length - 1] = `#${number}`;
  } else {
    const split = splitTrailingNumber(last);
    if (split) {
      parts[parts.length - 1] = split[0];
      parts.push(`#${split[1]}`);
    }
  }
  return parts.join(" ");
}

export function displayLabel(text: string): string {
  const parts = text.split(" ");
  const last = parts[parts.length - 1];
  if (parts.length < 2 || !last) return text;
  const digits = trailingDigits(last);
  const head = last.slice(0, last.length - digits.length);
  if (!digits || !head || !/^\p{L}+$/u.test(head)) return text;
  const number = trailingNumber(last);
  if (number === null) return text;
  parts[parts.length - 1] = `#${number}`;
  return parts.join(" ");
}

export function looksLikeLabel(text: string): boolean {
  const key = labelKey(text);
  const last = key.split(" ").pop() ?? "";
  return last.startsWith("#") && /\p{L}/u.test(key);
}

const EVERYONE = new Set(["tous", "tout", "toutes", "all", "everyone", "everybody", "group", "groupe"]);

export function isEveryone(group: string): boolean {
  return EVERYONE.has(fold(group));
}

export function isPrepLabel(label: string): boolean {
  const folded = fold(label);
  return folded.includes("prep") || folded.includes("warm up") || folded.includes("warmup");
}

export function isWeightsLabel(label: string): boolean {
  return fold(label).startsWith("weights") && !isPrepLabel(label);
}

function containsRun(haystack: string[], needle: string[]): boolean {
  if (needle.length > haystack.length) return false;
  for (let start = 0; start + needle.length <= haystack.length; start++) {
    if (needle.every((word, i) => haystack[start + i] === word)) return true;
  }
  return false;
}

/** A column heading against one of an athlete's My columns: loose, either way round. */
export function matchesGroup(group: string, name: string): boolean {
  const g = words(group);
  const n = words(name);
  if (g.length === 0 || n.length === 0) return false;
  if (g.join(" ") === n.join(" ")) return true;
  if (containsRun(g, n) || containsRun(n, g)) return true;
  const gt = g.join("");
  const nt = n.join("");
  if (Math.min(gt.length, nt.length) < 3) return false;
  return gt.includes(nt) || nt.includes(gt);
}

/** `Sprints #3` → `sprints`; free text is its own family. */
export function familyKey(label: string): string {
  return labelKey(label).replace(/\s*#\d+$/, "");
}

/** `Weights Prep #1` → `Weights Prep`, as the coach writes it. */
export function familyName(label: string): string {
  return displayLabel(label.trim()).replace(/\s*#\d+$/, "").trim();
}

export function labelNumber(label: string): number | null {
  const match = /#(\d+)$/.exec(labelKey(label));
  return match ? Number(match[1]) : null;
}

/** The next free number of a family: `Sprints #4` when #1–#3 exist. */
export function nextLabel(family: string, labels: string[]): string {
  const key = fold(family);
  let max = 0;
  for (const label of labels) {
    if (familyKey(label) !== key) continue;
    max = Math.max(max, labelNumber(label) ?? 0);
  }
  return `${family.trim()} #${max + 1}`;
}

export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}
